import React, { useEffect, useMemo } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import PropTypes from 'prop-types';
import has from 'lodash.has';

import { Div, Icon, Text } from '@pinpoint/design-kit';
import { queryHelper } from 'src/functions';
import { Button } from 'src/components';
import { ModelProp } from 'src/store/typings';

/**
 * EqualBucketsSelector - Buttons for selecting equal type
 *
 * @param {object} props - components props
 * @param {array} props.models - list of models
 * @param {('premiums'|'persons')} props.equalType - either premium or persons
 * @param {function} props.handleEqualTypeChange - the handler to set equalType
 * @param {('Deciles'|'Buckets')} props.equalCategory - for text description, either Deciles or Buckets
 * @param {boolean} props.disablePremiums - if premiums button should be disabled
 * @param {boolean} props.disablePersons - if persons button should be disabled
 */
function EqualBucketsSelector({
  models,
  equalType,
  handleEqualTypeChange,
  equalCategory,
  disablePremiums,
  disablePersons,
}) {
  const history = useHistory();
  const { search, pathname } = useLocation();
  const includesPersons = useMemo(
    () => models.some((model) => has(model, 'modelData.dataPayload.equal_persons')),
    [models]
  );

  // NOTE: the premiums button should be enabled only if all models have premiums otherwise it should show only persons
  const includesPremiums = useMemo(
    () =>
      models.every(
        (model) =>
          has(model, 'modelData.dataPayload.equal_premiums') || has(model, 'modelData.dataPayload.equal_premium')
      ),
    [models]
  );
  const showEqualBuckets = useMemo(() => includesPersons || includesPremiums, [includesPersons, includesPremiums]);

  useEffect(() => {
    queryHelper(history, (query) => {
      // When there's new query params parse for equal, handle cases where it needs to be updated
      if (showEqualBuckets) {
        const nextEqualType = query.get('equal');
        if (!nextEqualType) {
          query.set('equal', 'persons');
        } else if (nextEqualType === 'premiums' && !includesPremiums) {
          query.set('equal', 'persons');
        } else {
          handleEqualTypeChange(nextEqualType);
        }
      }
    });
  }, [history, search, pathname, showEqualBuckets, includesPremiums, handleEqualTypeChange]);

  if (!showEqualBuckets) {
    return null;
  }

  return (
    <>
      <Div flexGrow="1" />
      <Text textSize="input" m={{ r: '.5rem' }} textWeight="500">
        {equalCategory} by equal:
      </Text>
      <Button
        buttonStyle="tab"
        selected={equalType?.includes?.('person')}
        disabled={disablePersons || !includesPersons}
        data-test="persons-btn"
        onClick={() => {
          const nextParams = new URLSearchParams(search);
          if (nextParams.get('equal') !== 'persons') {
            nextParams.set('equal', 'persons');
            history.push(`${pathname}?${nextParams.toString()}`);
          }
        }}
      >
        <Icon name="Customer" color="white" m={{ r: '0.5rem' }} size="1rem" />
        PERSONS
      </Button>
      <Button
        buttonStyle="tab"
        selected={equalType?.includes?.('premium')}
        disabled={disablePremiums || !includesPremiums}
        data-test="premiums-btn"
        onClick={() => {
          const nextParams = new URLSearchParams(search);
          if (nextParams.get('equal') !== 'premiums') {
            nextParams.set('equal', 'premiums');
            history.push(`${pathname}?${nextParams.toString()}`);
          }
        }}
      >
        <Icon name="DollarsPremium" viewBox="0 0 18 18" color="white" m={{ r: '.5rem' }} />
        PREMIUMS
      </Button>
    </>
  );
}

EqualBucketsSelector.defaultProps = {
  models: [],
  equalCategory: 'Deciles',
  equalType: 'premiums',
  handleEqualTypeChange: () => null,
  disablePremiums: false,
  disablePersons: false,
};

EqualBucketsSelector.propTypes = {
  models: PropTypes.arrayOf(ModelProp),
  equalCategory: PropTypes.string,
  equalType: PropTypes.string,
  handleEqualTypeChange: PropTypes.func,
  disablePremiums: PropTypes.bool,
  disablePersons: PropTypes.bool,
};

export default React.memo(EqualBucketsSelector);
